import React, { useState } from 'react';
import { Layout, Menu, theme, Button, Dropdown } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  DashboardOutlined,
  ShopOutlined,
  DollarOutlined,
  MobileOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 确定当前选中的菜单项
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.includes('/employee') && !path.includes('/mobile-preview')) return ['employee'];
    if (path.includes('/customer')) return ['customer'];
    if (path.includes('/marketing-flow')) return ['4'];
    if (path.includes('/commission-config')) return ['commission-config'];
    if (path.includes('/commission-audit')) return ['commission-audit'];
    if (path.includes('/exception-order')) return ['exception-order'];
    if (path.includes('/commission')) return ['5'];
    if (path.includes('/data-monitoring')) return ['data-monitoring'];
    if (path.includes('/activity-center')) return ['activity-center'];
    if (path.includes('/butler-binding-details')) return ['butler-binding'];
    if (path.includes('/mobile-preview')) {
      if (path.includes('/profile')) return ['6-3'];
      if (path.includes('/bind-butler-success')) return ['6-4'];
      return ['6'];
    }
    return ['1']; // 默认仪表盘
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: '1',
      label: '个人中心',
      icon: <UserOutlined />,
    },
    {
      key: '2',
      label: '个人设置',
      icon: <SettingOutlined />,
    },
    {
      type: 'divider',
    },
    {
      key: '3',
      label: '退出登录',
      icon: <LogoutOutlined />,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        theme="light"
        style={{
          boxShadow: '2px 0 8px 0 rgba(29,35,41,.05)'
        }}
      >
        <div className="logo" style={{ 
          height: '64px', 
          padding: '16px', 
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: collapsed ? '16px' : '18px',
          color: '#529C8A',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {collapsed ? '秋果' : '秋果酒店管理系统'}
        </div>
        <Menu
          theme="light"
          mode="inline"
          defaultSelectedKeys={getSelectedKey()}
          items={[
            {
              key: '1',
              icon: <DashboardOutlined />,
              label: '仪表盘',
              onClick: () => navigate('/')
            },
            {
              key: 'employee',
              icon: <TeamOutlined />,
              label: '员工管理',
              onClick: () => navigate('/employee')
            },
            {
              key: 'customer',
              icon: <UserOutlined />,
              label: '会员管理',
              onClick: () => navigate('/customer')
            },
            {
              key: 'marketing',
              icon: <ShopOutlined />,
              label: '营销管理',
              children: [
                {
                  key: '4',
                  label: '营销流程',
                  onClick: () => navigate('/marketing-flow')
                },
                {
                  key: 'activity-center',
                  label: '活动中心',
                  onClick: () => navigate('/activity-center')
                },
                {
                  key: 'butler-binding',
                  label: '管家绑定明细',
                  onClick: () => navigate('/butler-binding-details')
                },
                {
                  key: 'butler-performance',
                  label: '管家业绩分析',
                  onClick: () => navigate('/butler-performance')
                },
                {
                  key: 'consumption-conversion',
                  label: '消费转化分析',
                  onClick: () => navigate('/consumption-conversion')
                },
                {
                  key: 'repurchase-retention',
                  label: '复购留存分析',
                  onClick: () => navigate('/repurchase-retention')
                }
              ]
            },
            {
              key: 'commission-group',
              icon: <DollarOutlined />,
              label: '佣金管理',
              children: [
                {
                  key: '5',
                  label: '佣金记录',
                  onClick: () => navigate('/commission')
                },
                {
                  key: 'commission-config',
                  label: '分佣配置',
                  onClick: () => navigate('/commission-config')
                },
                {
                  key: 'commission-audit',
                  label: '提现记录',
                  onClick: () => navigate('/commission-audit')
                },
                {
                  key: 'exception-order',
                  label: '异常订单处理',
                  onClick: () => navigate('/exception-order')
                }
              ]
            },
            {
              key: 'data-monitoring',
              icon: <DashboardOutlined />,
              label: '数据监控',
              onClick: () => navigate('/data-monitoring')
            },
            {
              key: '6',
              icon: <MobileOutlined />,
              label: '移动端预览',
              children: [
                {
                  key: '6-3',
                  label: '个人中心',
                  onClick: () => navigate('/mobile-preview/profile')
                },
                {
                  key: '6-4',
                  label: '成功绑定管家',
                  onClick: () => navigate('/mobile-preview/bind-butler-success')
                },
                {
                  key: '6-5',
                  label: '通知中心',
                  onClick: () => navigate('/mobile-preview/inventory-alerts')
                }
              ]
            },
          ]}
        />
      </Sider>
      <Layout>
        <Header style={{ 
          padding: 0, 
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <div style={{ marginRight: '24px' }}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <span style={{ cursor: 'pointer' }}>
                <UserOutlined style={{ marginRight: 8 }} />
                <span>管理员</span>
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            overflowY: 'auto'
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout; 